import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Dialog,
  FormField,
  Modal,
  Select,
  Spinner,
  Table,
  TextInput,
  useToast,
  type TableColumn,
} from '@bravemove/ui-web';
import type { AdminRole, AdminStatus } from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';
import { ApiError, type AdminUserSummary, type ListEnvelope } from '../auth/types';
import { useAuth } from '../auth/useAuth';
import { translateApiError } from '../auth/errors';
import { PageHeader } from '../components/PageHeader';

const PAGE_SIZE = 25;

const ROLE_VALUES: readonly AdminRole[] = [
  'super_admin',
  'support',
  'finance',
  'content_moderator',
];

interface InviteFormValues {
  readonly email: string;
  readonly role: AdminRole;
  readonly displayName: string;
}

interface EditFormValues {
  readonly role: AdminRole;
  readonly displayName: string;
}

export function AdminUsersPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const { adminUser } = useAuth();
  const [rows, setRows] = useState<readonly AdminUserSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminUserSummary | null>(null);
  const [disableTarget, setDisableTarget] = useState<AdminUserSummary | null>(null);
  const [resendTarget, setResendTarget] = useState<AdminUserSummary | null>(null);
  const [resetTotpTarget, setResetTotpTarget] = useState<AdminUserSummary | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<AdminUserSummary | null>(null);
  const [reinstateTarget, setReinstateTarget] = useState<AdminUserSummary | null>(null);

  const fetchUsers = useCallback(
    async (nextOffset: number) => {
      setLoading(true);
      setError(null);
      try {
        const body = await adminFetch<ListEnvelope<AdminUserSummary>>(
          `/admin/users${buildQuery({ limit: PAGE_SIZE, offset: nextOffset })}`,
        );
        setRows(body.items);
        setTotal(body.total ?? body.items.length);
        setOffset(body.offset);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    // Fetch-on-mount; setState happens inside fetchUsers which is the
    // canonical async-load pattern. The lint rule is overzealous here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchUsers(0);
  }, [fetchUsers]);

  const columns = useMemo<readonly TableColumn<AdminUserSummary>[]>(() => {
    return [
      {
        key: 'email',
        header: t('admin:adminUsers.table.email'),
        cell: (row) => <span className="font-mono text-[12.5px] text-ink">{row.email}</span>,
      },
      {
        key: 'name',
        header: t('admin:adminUsers.table.name'),
        cell: (row) => row.displayName ?? '—',
      },
      {
        key: 'role',
        header: t('admin:adminUsers.table.role'),
        cell: (row) => <Badge tone="info">{t(`admin:roles.${row.role}`)}</Badge>,
      },
      {
        key: 'status',
        header: t('admin:adminUsers.table.status'),
        cell: (row) => <StatusBadge status={row.status} />,
      },
      {
        key: 'totpEnrolled',
        header: t('admin:adminUsers.table.totpEnrolled'),
        cell: (row) => (row.totpEnrolledAt ? formatDate(row.totpEnrolledAt, locale) : '—'),
      },
      {
        key: 'lastLogin',
        header: t('admin:adminUsers.table.lastLogin'),
        cell: (row) => (row.lastLoginAt ? formatDate(row.lastLoginAt, locale) : '—'),
      },
      {
        key: 'actions',
        header: t('admin:adminUsers.table.actions'),
        align: 'end',
        cell: (row) => (
          <div className="inline-flex gap-1">
            <Button size="xs" variant="ghost" onClick={() => setEditTarget(row)}>
              {t('admin:common.edit')}
            </Button>
            {row.status === 'pending' ? (
              <Button size="xs" variant="ghost" onClick={() => setResendTarget(row)}>
                {t('admin:common.resend')}
              </Button>
            ) : null}
            {row.totpEnrolledAt ? (
              <Button size="xs" variant="ghost" onClick={() => setResetTotpTarget(row)}>
                {t('admin:adminUsers.resetTotp.confirm')}
              </Button>
            ) : null}
            {row.id !== adminUser?.id && row.status === 'active' ? (
              <Button size="xs" variant="ghost" onClick={() => setSuspendTarget(row)}>
                {t('admin:common.suspend')}
              </Button>
            ) : null}
            {row.status === 'suspended' ? (
              <Button size="xs" variant="ghost" onClick={() => setReinstateTarget(row)}>
                {t('admin:common.reinstate')}
              </Button>
            ) : null}
            {row.id !== adminUser?.id && row.status !== 'disabled' ? (
              <Button size="xs" variant="ghost" onClick={() => setDisableTarget(row)}>
                {t('admin:common.disable')}
              </Button>
            ) : null}
          </div>
        ),
      },
    ];
  }, [t, locale, adminUser?.id]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('admin:adminUsers.title')}
        subtitle={t('admin:adminUsers.subtitle')}
        actions={
          <Button variant="primary" icon="plus" onClick={() => setInviteOpen(true)}>
            {t('admin:adminUsers.inviteCta')}
          </Button>
        }
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size={28} />
        </div>
      ) : (
        <Table
          data={rows}
          columns={columns}
          rowKey={(row) => row.id}
          empty={t('admin:adminUsers.table.empty')}
        />
      )}

      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-between text-[12.5px] text-ink-2">
          <div>
            {t('admin:common.page')} {Math.floor(offset / PAGE_SIZE) + 1} {t('admin:common.of')}{' '}
            {Math.max(1, Math.ceil(total / PAGE_SIZE))}
          </div>
          <div className="inline-flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={offset === 0 || loading}
              onClick={() => void fetchUsers(Math.max(0, offset - PAGE_SIZE))}
            >
              {t('admin:common.previous')}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={offset + PAGE_SIZE >= total || loading}
              onClick={() => void fetchUsers(offset + PAGE_SIZE)}
            >
              {t('admin:common.next')}
            </Button>
          </div>
        </div>
      ) : null}

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={() => {
          toast.push({
            tone: 'success',
            title: t('admin:adminUsers.invite.successTitle'),
            description: t('admin:adminUsers.invite.successBody'),
          });
          setInviteOpen(false);
          void fetchUsers(0);
        }}
      />

      <EditModal
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSuccess={() => {
          toast.push({ tone: 'success', title: t('admin:adminUsers.edit.successTitle') });
          setEditTarget(null);
          void fetchUsers(offset);
        }}
      />

      <ConfirmDialog
        target={disableTarget}
        onClose={() => setDisableTarget(null)}
        onConfirm={async () => {
          if (!disableTarget) return;
          await adminFetch(`/admin/users/${disableTarget.id}`, { method: 'DELETE' });
          toast.push({ tone: 'success', title: t('admin:adminUsers.disable.successTitle') });
          setDisableTarget(null);
          void fetchUsers(offset);
        }}
        titleKey="admin:adminUsers.disable.title"
        bodyKey="admin:adminUsers.disable.body"
        confirmKey="admin:adminUsers.disable.confirm"
        destructive
      />

      <ConfirmDialog
        target={resendTarget}
        onClose={() => setResendTarget(null)}
        onConfirm={async () => {
          if (!resendTarget) return;
          await adminFetch(`/admin/users/${resendTarget.id}/resend-invite`, {
            method: 'POST',
          });
          toast.push({
            tone: 'success',
            title: t('admin:adminUsers.resendInvite.successTitle'),
          });
          setResendTarget(null);
          void fetchUsers(offset);
        }}
        titleKey="admin:adminUsers.resendInvite.title"
        bodyKey="admin:adminUsers.resendInvite.body"
        confirmKey="admin:adminUsers.resendInvite.confirm"
      />

      <ConfirmDialog
        target={resetTotpTarget}
        onClose={() => setResetTotpTarget(null)}
        onConfirm={async () => {
          if (!resetTotpTarget) return;
          await adminFetch(`/admin/users/${resetTotpTarget.id}`, {
            method: 'PATCH',
            body: { totpReset: true },
          });
          toast.push({ tone: 'success', title: t('admin:adminUsers.resetTotp.successTitle') });
          setResetTotpTarget(null);
          void fetchUsers(offset);
        }}
        titleKey="admin:adminUsers.resetTotp.title"
        bodyKey="admin:adminUsers.resetTotp.body"
        confirmKey="admin:adminUsers.resetTotp.confirm"
        destructive
      />

      <ConfirmDialog
        target={suspendTarget}
        onClose={() => setSuspendTarget(null)}
        onConfirm={async () => {
          if (!suspendTarget) return;
          await adminFetch(`/admin/users/${suspendTarget.id}/suspend`, { method: 'POST' });
          toast.push({ tone: 'success', title: t('admin:adminUsers.suspend.successTitle') });
          setSuspendTarget(null);
          void fetchUsers(offset);
        }}
        titleKey="admin:adminUsers.suspend.title"
        bodyKey="admin:adminUsers.suspend.body"
        confirmKey="admin:adminUsers.suspend.confirm"
        destructive
      />

      <ConfirmDialog
        target={reinstateTarget}
        onClose={() => setReinstateTarget(null)}
        onConfirm={async () => {
          if (!reinstateTarget) return;
          await adminFetch(`/admin/users/${reinstateTarget.id}/reinstate`, { method: 'POST' });
          toast.push({ tone: 'success', title: t('admin:adminUsers.reinstate.successTitle') });
          setReinstateTarget(null);
          void fetchUsers(offset);
        }}
        titleKey="admin:adminUsers.reinstate.title"
        bodyKey="admin:adminUsers.reinstate.body"
        confirmKey="admin:adminUsers.reinstate.confirm"
      />
    </div>
  );
}

function StatusBadge({ status }: { status: AdminStatus }) {
  const { t } = useI18n();
  const tone =
    status === 'active'
      ? 'success'
      : status === 'disabled'
        ? 'danger'
        : status === 'suspended'
          ? 'warning'
          : status === 'pending_totp'
            ? 'warning'
            : 'info';
  return <Badge tone={tone}>{t(`admin:statuses.${status}`)}</Badge>;
}

function InviteModal({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useI18n();
  const [serverError, setServerError] = useState<string | null>(null);
  const schema = z.object({
    email: z.string().email(t('admin:validation.email')),
    role: z.enum(ROLE_VALUES as unknown as [AdminRole, ...AdminRole[]]),
    displayName: z.string().max(120),
  });
  const form = useForm<InviteFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', role: 'support', displayName: '' },
  });

  useEffect(() => {
    if (open) {
      form.reset({ email: '', role: 'support', displayName: '' });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setServerError(null);
    }
  }, [open, form]);

  const submit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await adminFetch('/admin/users/invite', {
        method: 'POST',
        body: {
          email: values.email,
          role: values.role,
          ...(values.displayName ? { displayName: values.displayName } : {}),
        },
      });
      onSuccess();
    } catch (err) {
      setServerError(translateApiError(err, t));
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:adminUsers.invite.title')}
      description={t('admin:adminUsers.invite.subtitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('admin:common.cancel')}
          </Button>
          <Button
            variant="primary"
            type="button"
            loading={form.formState.isSubmitting}
            onClick={() => void submit()}
          >
            {t('admin:adminUsers.invite.submit')}
          </Button>
        </>
      }
    >
      {serverError ? (
        <Banner tone="danger" className="mb-4">
          {serverError}
        </Banner>
      ) : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="space-y-4"
      >
        <FormField
          label={t('admin:common.email')}
          error={form.formState.errors.email?.message}
          required
        >
          <TextInput type="email" {...form.register('email')} />
        </FormField>
        <FormField label={t('admin:common.displayName')}>
          <TextInput {...form.register('displayName')} />
        </FormField>
        <FormField label={t('admin:common.role')} required>
          <Select
            {...form.register('role')}
            options={ROLE_VALUES.map((r) => ({ label: t(`admin:roles.${r}`), value: r }))}
          />
        </FormField>
      </form>
    </Modal>
  );
}

function EditModal({
  target,
  onClose,
  onSuccess,
}: {
  target: AdminUserSummary | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useI18n();
  const [serverError, setServerError] = useState<string | null>(null);
  const schema = z.object({
    role: z.enum(ROLE_VALUES as unknown as [AdminRole, ...AdminRole[]]),
    displayName: z.string().max(120),
  });
  const form = useForm<EditFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { role: 'support', displayName: '' },
  });

  useEffect(() => {
    if (target) {
      form.reset({ role: target.role, displayName: target.displayName ?? '' });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setServerError(null);
    }
  }, [target, form]);

  const submit = form.handleSubmit(async (values) => {
    if (!target) return;
    setServerError(null);
    try {
      await adminFetch(`/admin/users/${target.id}`, {
        method: 'PATCH',
        body: {
          role: values.role,
          displayName: values.displayName || null,
        },
      });
      onSuccess();
    } catch (err) {
      setServerError(translateApiError(err, t));
    }
  });

  return (
    <Modal
      open={!!target}
      onClose={onClose}
      title={t('admin:adminUsers.edit.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('admin:common.cancel')}
          </Button>
          <Button
            variant="primary"
            type="button"
            loading={form.formState.isSubmitting}
            onClick={() => void submit()}
          >
            {t('admin:adminUsers.edit.submit')}
          </Button>
        </>
      }
    >
      {serverError ? (
        <Banner tone="danger" className="mb-4">
          {serverError}
        </Banner>
      ) : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="space-y-4"
      >
        <FormField label={t('admin:common.email')}>
          <TextInput value={target?.email ?? ''} disabled readOnly />
        </FormField>
        <FormField label={t('admin:common.displayName')}>
          <TextInput {...form.register('displayName')} />
        </FormField>
        <FormField label={t('admin:common.role')} required>
          <Select
            {...form.register('role')}
            options={ROLE_VALUES.map((r) => ({ label: t(`admin:roles.${r}`), value: r }))}
          />
        </FormField>
      </form>
    </Modal>
  );
}

function ConfirmDialog({
  target,
  onClose,
  onConfirm,
  titleKey,
  bodyKey,
  confirmKey,
  destructive,
}: {
  target: AdminUserSummary | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  titleKey: string;
  bodyKey: string;
  confirmKey: string;
  destructive?: boolean;
}) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (target) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setError(null);
      setLoading(false);
    }
  }, [target]);

  return (
    <Dialog
      open={!!target}
      onClose={onClose}
      title={t(titleKey)}
      {...(target ? { description: target.email } : {})}
      confirmLabel={t(confirmKey)}
      cancelLabel={t('admin:common.cancel')}
      destructive={destructive ?? false}
      loading={loading}
      onConfirm={async () => {
        setLoading(true);
        setError(null);
        try {
          await onConfirm();
        } catch (err) {
          if (err instanceof ApiError) {
            setError(err.body?.error?.message ?? err.message);
          } else {
            setError(t('admin:errors.generic'));
          }
        } finally {
          setLoading(false);
        }
      }}
    >
      <p className="text-[13px] text-ink-2 leading-snug">{t(bodyKey)}</p>
      {error ? (
        <Banner tone="danger" className="mt-3">
          {error}
        </Banner>
      ) : null}
    </Dialog>
  );
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}
